/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { useGetComponentCmsDocQuery } from 'app/services/ahuComponentFormsApi';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { store } from 'app/store';
import type { IAhuAccessoryRow, IAhuBoardSection } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { guardSelect } from '../cascadeCommit';
import { UDA_AIR_COMMIT_KEYS, hasAnyKey, selectBoardSyncOn } from '../commitAirConditions';
import {
  validateAccessoryOnChange,
  validateSectionAccessories,
} from '../../validation/validationController';
import { FormModel, useFormModel } from '../autoSaveModel';
import { CatalogueFieldEngine, useCatalogueFields } from '../catalogueField';
import type { ComponentFormShim } from '../componentFormTypes';
import { notifyFormShims, registerFormShim } from '../componentFormTypes';
import { selectValueToId, validateNumberField } from '../formUtils';
import type { AccessoryTypeConfig } from './accessoryFormConfigs';
import { accessoryFormsApi } from './accessoryFormsApi';
import { AccessoryPositionModel } from './positionModel';
import { MountingModel, type MountingPlaneRow } from './mountingModel';
import { deriveAwDims, type MountingSectionDims } from './mountingMath';
import {
  DEFAULT_FACE_VELOCITY,
  readDesiredFaceVelocity,
  writeDesiredFaceVelocity,
  type AutoSizeFn,
} from './useAutoSize';
import { buildAccessoryCanvasData, buildDrainPanCanvasData, accessoryDrawGate } from './accessoryDrawSync';

/**
 * Phase 6e2b-1 — useAccessoryForm: the React home of
 * base_classes/accessory_view.js (2,248 lines, read in full) over the §49.2
 * auto-save engine + the §51.3 position store + the §51.4 mounting model.
 *
 * Parity map (§53.1):
 *  - #299–304 auto-save reuse + accessory guards/side-effects (FormModel +
 *    accessoryAfterSave hook: tunnelLugs:recalcPlacement, position refetch,
 *    sublist-grey lift, changeOnlyPerformance/uda_render = React no-ops since
 *    controlled inputs never lose state on re-render);
 *  - #329 boot rules (position load gates, fresh-create forceSync, duplicate
 *    flow now owned by boardActions.duplicateAccessoryAny — 6e1);
 *  - #330 render pipeline (data fixups live in the per-type form bodies);
 *  - #331 autoSize engine seam (config — unexercised by the 6e2b-1 types);
 *  - #332 useAccessoryPosition + createPosition state machine (hood
 *    mount/unmount transitions are 6e2b-2 EXTENSION POINTS — see
 *    createPosition below);
 *  - #334 drawOnCanvas JSON-diff contract (accessoryDrawSync);
 *  - #337 socket listeners (positioningoncabinet 100 ms no_fetch;
 *    ahuaccessorydrainpan put);
 *  - #338 teardown; #339 tag edits; #311 changeSelectValue accessory variant
 *    (DOM-scan, armed by data-new/deselct:model — gated on userArmed);
 *  - #352 loaded_accessories_count/360-unlock: NOT replicated — both sides
 *    are React; the 6c2 canvas draw paths clear `rotateEnabled` in the store
 *    (canvas3dGeometry.onRotateAvailabilityClear), which is the same signal.
 *
 * DATA SAFETY: FormModel + AccessoryPositionModel both carry the 6e2a
 * `userArmed` gate — zero mutating calls until a real user gesture.
 */

export interface AccessoryFormArgs {
  config: AccessoryTypeConfig;
  row: IAhuAccessoryRow;
  section: IAhuBoardSection;
  /** tunnel row (aw_width/aw_height derivation — section.js:107/:122). */
  tunnel?: { width?: unknown; height?: unknown } | null;
  /** row expanded (accessory_activity checked) — drives the openGroup
   * click-all-labeled-subtabs behavior (tab.accessories.js:684). */
  expanded: boolean;
  /** tmpl subtab inputs that HAVE a label (the openGroup click targets). */
  labeledSubtabs: string[];
  /** per-type template-data getter (shim member #12). */
  dataGetterForRendering?: (stagingPosition?: unknown, stagingModel?: unknown) => Record<string, unknown>;
  /** §49.6 autoSize seam (6e2b-2 types) — parity 331. */
  autoSize?: AutoSizeFn;
  autoSizeGetState?: (data: Record<string, unknown>) => Record<string, unknown>;
  getFaceVelocityArea?: (data: Record<string, unknown>) => number | null;
  defaultFaceVelocity?: number | string;
  /**
   * 6e2b-2 — section internal walls (sorted, IW-name-mapped, plane-filtered;
   * built by useSectionInternalWalls). Feeds the damper/louver/access_door/
   * opening dataGetter select options AND the drawOnCanvas plane>6 lookup
   * (parity 334).
   */
  internalWalls?: Array<Record<string, unknown>>;
}

export interface AccessoryFormApi {
  model: FormModel | null;
  position: AccessoryPositionModel | null;
  mounting: MountingModel | null;
  /** unwrapped per-type CMS params doc (cms_accessory). */
  cmsAccessory: Record<string, unknown> | undefined;
  catalogueFields: Record<string, CatalogueFieldEngine>;
  subtabs: Record<string, boolean>;
  toggleSubtab: (name: string, open: boolean) => void;
  /** §49.6 onModelFieldChange (accessory variant). */
  commitModelField: (
    name: string,
    raw: string | null,
    opts?: { min?: number; max?: number; formatter?: 'decimal' | 'integer' }
  ) => void;
  commitModelCheckbox: (name: string, checked: boolean) => void;
  commitParamsField: (targetField: string, name: string, raw: string) => void;
  /** §49.6 onPositionFieldChange — x/y inputs + default_pos checkboxes. */
  commitPositionField: (
    name: string,
    raw: string | null,
    opts?: { positionOnly?: boolean }
  ) => void;
  commitPositionCheckbox: (name: string, checked: boolean) => void;
  /** mounting_plane_id select (creates the position on first pick).
   * 6e2b-2: `mountingValue` = the option's data-value ('hood'/'fan_intake'/
   * plane value) — drives the hood mount/unmount transitions. */
  commitMountingPlane: (
    raw: string,
    positionType: string | undefined,
    mountingValue?: string
  ) => void;
  /** §50.2 shim member #10. */
  createPosition: (
    positionType: string,
    mountingPlaneId: number | string,
    mountingValue?: unknown,
    changeTagName?: string,
    extraData?: Record<string, unknown>
  ) => Promise<AccessoryPositionModel | undefined>;
  /** mounting popup state (location_btn → open; §51.4 base popup). */
  popupOpen: boolean;
  openMountingPopup: () => void;
  closeMountingPopup: () => void;
  /** cms_accessory.mounting_plane rows. */
  mountingPlanes: MountingPlaneRow[];
  /** resolved plane row for the current position (dataGetter contract). */
  currentPlane: MountingPlaneRow | undefined;
  awDims: MountingSectionDims;
  desiredFaceVelocity: string;
  loading: boolean;
  filterFieldsDisabled: boolean;
  shim: ComponentFormShim | null;
  /** form root ref (callback-ref onto the type form's root `<form>`) —
   * changeSelectValue DOM scan + Tab-key nav host. Moved off an intermediate
   * div: legacy `.accessory-wrapper > form > .sublist` CSS expansion requires
   * `form` as DIRECT wrapper child (6e2b verifier fix — bodies were stuck at
   * max-height 0). */
  formRef: React.MutableRefObject<HTMLElement | null>;
  // ---- 6e2b-2 complex-type surface ----
  /** damper: currently mounted on a hood (mounting_plane contains 'Hood'). */
  hoodIsMounting: boolean;
  /** damper web handlers flip the hood flag (hood:removed / update:params). */
  markHoodIsMounting: (on: boolean) => void;
  /** damper: `.for_hood_mounting` preloader (hood-constrained CMS in flight). */
  forHoodMounting: boolean;
  /** damper: fan_intake catalogueFieldsIntake swap state (§49.6:200-207). */
  intakeMode: boolean;
  setIntakeMode: (on: boolean) => void;
  /** damper CMS rebuild (update:damper:params web — dampers_cms semantics:
   * RTKQ caches by URL + dedups in-flight, replacing the legacy module map). */
  setCmsURLOverride: (url: string | null) => void;
  /** opening: `.sublist` greying during every PATCH (§49.6:1233). */
  sublistsGreyed: boolean;
  /** IW list feed (forms resolve it after the hook — see internalWallsRef). */
  setInternalWalls: (list: Array<Record<string, unknown>> | undefined) => void;
  /** §49.6 onAutoSze — manual `.auto-size` click (force) / render attempt. */
  runAutoSize: (force: boolean) => void;
  autoSizeDisabled: boolean;
  setDesiredFaceVelocity: (value: string) => void;
}

const POSITION_BASE_FOR_TYPE: Record<string, '' | 'on_cabinet' | 'on_internal_walls'> = {
  on_cabinet: 'on_cabinet',
  on_internal_wall: 'on_internal_walls',
};

/** §49.6:828 — types whose apd/air_stream deltas re-fire fetch:fans:esp. */
const ESP_INFO_TYPES = new Set([
  'user_defined_accessory',
  'opening',
  'louver',
  'damper',
  'hood',
]);

/**
 * QA batch 8 #3c follow-up — one-shot suppression of the openGroup click-all
 * for the ADD path's programmatic expand. Legacy's add rendered the new row
 * checked WITHOUT a change event (api.js:413 fires before the list renders →
 * tab.accessories.js:246 render-checked branch), so openGroup (:684) never
 * ran and child subtabs stayed collapsed; only a MANUAL activity click runs
 * the open-all. TabAccessories' add path marks the id here right before its
 * selectRow; the owning form's expand effect consumes-and-clears the mark.
 */
export const suppressOpenGroupOnce = { accessoryId: null as number | null };

/**
 * QA 2026-09-02 — the ADD path's `data-new` arming. Legacy stamps
 * `data-new="true"` on the row it just created (tab.accessories.js:230-241 —
 * the initial page-load render passes `is_new=false`, so this is an add-only
 * marker), the form reads it once (accessory_view.js:372 → `self.allow = true`)
 * and post-render runs the FULL changeSelectValue instead of the empties-only
 * variant (:1102). That single pass writes any sole-option dropdown into the
 * params JSON, which trips the catalogue's narrow-to-one auto-pick and pulls
 * the whole master row down — which is how a freshly added light arrived
 * pre-filled on dev. React had no port of it, so the defaults only appeared
 * once something else armed the scan (the Reset button, via `deselct:model`).
 * `boardActions.createAccessoryAny` marks the id; the form consumes it once.
 * A Set rather than a single slot because legacy's marker was per-row (an
 * attribute on the `<li>`), so two adds in quick succession both stay armed.
 */
export const newlyAddedAccessoryIds = new Set<number>();

/**
 * The accessory types whose selector dialog auto-opens the moment they're
 * created, so the user lands straight in the dialog instead of an empty
 * accordion row. See the ADD-path arming effect below.
 *
 * Legacy does this generically rather than by a list: tab.accessories.js:362-367
 * fires, 1500ms after a newly-added accessory's view is constructed,
 *   document.querySelector(`#forms_accessory_${id} label.active-mode`)?.click()
 * for all 15 types it handles. The `?.` is the real gate — the click only does
 * anything for the accessories whose form.tmpl actually renders a
 * `label.active-mode`, and that is exactly five of them:
 *   - opening/damper/hood/louver — form.tmpl stamps `active-mode` STATICALLY
 *     (acc {opening:7, damper:6, hood:5, louver:5});
 *   - uvc — form.tmpl:11 stamps it CONDITIONALLY,
 *     `{{#ifCompare ../model.select_mode_id '==' id}}class="active-mode uvc"`,
 *     inside an `{{#each uvc_selection_modes}}` filtered to `value != 'manual'`.
 *     That leaves one label (Rating, select_mode 2) and
 *     `AHUAccessoryUVC.save()` (models_accessory_uvc.py:1623-1629) forces
 *     `select_mode='rating'` whenever it is unset — so on a fresh UVC the
 *     condition is always true and the label always carries `active-mode`.
 *     Verified on dev3: all 537 ksdm_ahu_accessory_uvc rows are select_mode_id 2.
 * Every other accessory's tmpl has no such label, so its click is a no-op.
 *
 * uvc was missing here, which is the whole of the "Rating dialog doesn't open
 * after adding a UV-C" report — the other four were already right.
 */
const AUTO_OPEN_SELECTOR_TYPES = new Set(['opening', 'damper', 'hood', 'louver', 'uvc']);

export function useAccessoryForm(args: AccessoryFormArgs): AccessoryFormApi {
  const { config, row, section, tunnel, expanded } = args;
  const dispatch = useAppDispatch();
  const [, forceRender] = useState(0);
  const bump = useCallback(() => forceRender((v) => v + 1), []);
  const formRef = useRef<HTMLElement | null>(null);

  // ---- concrete row detail (GET ahu/<plural>/<id> — §20.6 #54) ----
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    let cancelled = false;
    // fresh-created rows carry `label` — legacy skips the fetch + forceSync
    // (§49.6:381-384). The generic row from getSectionAccessories is thin, so
    // we fetch unless the row was created this session (has `label`).
    const newCreated = Object.prototype.hasOwnProperty.call(row, 'label') && row.label != null && row.id == null;
    if (newCreated) {
      setDetail({ ...row });
      return undefined;
    }
    dispatch(
      ahuBoardApi.endpoints.getAccessoryDetail.initiate(
        { plural: config.plural, id: row.id },
        { subscribe: false, forceRefetch: true }
      )
    )
      .unwrap()
      .then((res) => {
        if (!cancelled) setDetail(res as Record<string, unknown>);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dispatch, config.plural, row.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- per-type CMS params doc (cms_accessory) ----
  // 6e2b-2 — damper rebuilds its CMS doc from hood-constrained / fan-intake
  // URLs (setCMSAccessory, §51.2 web). RTKQ keys the cache by URL and dedups
  // in-flight fetches — the legacy `dampers_cms` stateStorage + module-level
  // pending map semantics.
  const [cmsURLOverride, setCmsURLOverride] = useState<string | null>(null);
  const effectiveCmsURL = cmsURLOverride ?? config.cmsAccessoryURL ?? '';
  const { data: rawCmsDoc, isFetching: cmsFetching } = useGetComponentCmsDocQuery(
    effectiveCmsURL,
    { skip: !effectiveCmsURL }
  );
  const cmsAccessory = useMemo(() => {
    if (!rawCmsDoc) return undefined;
    if (config.cmsAccessoryKey === null) return rawCmsDoc; // dog_house whole doc
    const key = config.cmsAccessoryKey ?? config.type;
    const inner = (rawCmsDoc as Record<string, unknown>)[key];
    if (inner && typeof inner === 'object') return inner as Record<string, unknown>;
    // single-key unwrap fallback (same rule as the component chassis).
    const keys = Object.keys(rawCmsDoc);
    if (keys.length === 1 && typeof (rawCmsDoc as Record<string, unknown>)[keys[0]] === 'object') {
      return (rawCmsDoc as Record<string, unknown>)[keys[0]] as Record<string, unknown>;
    }
    return rawCmsDoc as Record<string, unknown>;
  }, [rawCmsDoc, config.cmsAccessoryKey, config.type]);
  const cmsAccessoryRef = useRef<Record<string, unknown> | undefined>(undefined);
  cmsAccessoryRef.current = cmsAccessory;

  const mountingPlanes = useMemo(
    () => ((cmsAccessory?.mounting_plane as MountingPlaneRow[]) ?? []),
    [cmsAccessory]
  );
  const mountingPlanesRef = useRef<MountingPlaneRow[]>([]);
  mountingPlanesRef.current = mountingPlanes;

  const awDims = useMemo(
    () => deriveAwDims(section as Record<string, unknown>, tunnel),
    [section, tunnel]
  );
  const awDimsRef = useRef(awDims);
  awDimsRef.current = awDims;

  // ---- view back-reference (§49.2 view.* touches) ----
  const viewRef = useRef({
    rate: false,
    withRating: false,
    catalogueTargetFields: config.catalogueFields.map((f) => f.targetField),
    disableFilterFields: () => undefined as void,
    enableFilterFields: () => undefined as void,
    render: () => undefined as void,
  });
  viewRef.current.render = bump;
  const [filterFieldsDisabled, setFilterFieldsDisabled] = useState(false);
  // ---- 6e2b-2 complex-type state ----
  /** damper mounted on hood (legacy hood_is_mounting). */
  const [hoodIsMounting, setHoodIsMounting] = useState(false);
  const hoodIsMountingRef = useRef(false);
  const markHoodIsMounting = useCallback((on: boolean) => {
    hoodIsMountingRef.current = on;
    setHoodIsMounting(on);
  }, []);
  /** damper fan_intake field-set swap (catalogueFieldsIntake). */
  const [intakeMode, setIntakeMode] = useState(false);
  /** opening PATCH-flight sublist greying (§49.6:1233). */
  const [sublistsGreyed, setSublistsGreyed] = useState(false);
  /** §49.6 autoSize: desired face velocity (localStorage side channel #349). */
  const [desiredFaceVelocity, setDesiredFaceVelocityState] = useState(() =>
    readDesiredFaceVelocity(row.id, String(args.defaultFaceVelocity ?? DEFAULT_FACE_VELOCITY))
  );
  const desiredFaceVelocityRef = useRef(desiredFaceVelocity);
  desiredFaceVelocityRef.current = desiredFaceVelocity;
  /** `_autoSizeSnapshot` → auto_size_disabled (parity 331). */
  const autoSizeSnapshot = useRef<Record<string, unknown> | null>(null);
  const [autoSizeDisabled, setAutoSizeDisabled] = useState(false);
  // IW list for the draw effect + dataGetter: the per-type forms resolve it
  // AFTER this hook runs (useSectionInternalWalls needs cmsAccessory), so it
  // arrives through setInternalWalls during the form's render — the ref is
  // current before the post-render draw effect fires.
  const internalWallsRef = useRef<Array<Record<string, unknown>> | undefined>(undefined);
  if (args.internalWalls !== undefined) internalWallsRef.current = args.internalWalls;
  const setInternalWalls = useCallback((list: Array<Record<string, unknown>> | undefined) => {
    internalWallsRef.current = list;
  }, []);
  viewRef.current.disableFilterFields = () => setFilterFieldsDisabled(true);
  viewRef.current.enableFilterFields = () => {
    setFilterFieldsDisabled(false);
    setSublistsGreyed(false);
    bump();
  };

  // ---- position + mounting refs (created before the model needs them) ----
  const positionRef = useRef<AccessoryPositionModel | null>(null);
  const mountingRef = useRef<MountingModel | null>(null);
  /** §49.2 fetchPosition flag (set by height/width/blade edits). */
  const fetchPositionFlag = useRef(false);
  /**
   * 6e2c-3 fix — has the position boot below SETTLED? Backs the shim's
   * `positionReady()` so the accessory selector can wait for the live position
   * instead of cloning `{}` (see ComponentFormShim.positionReady). Flipped when
   * the boot effect resolves its fetch AND when it decides no position is
   * coming; never un-flipped, so a waiter cannot hang.
   */
  const positionSettled = useRef(false);
  const settlePosition = useCallback(() => {
    if (positionSettled.current) return;
    positionSettled.current = true;
    notifyFormShims();
  }, []);

  // ---- the §49.2 model (accessory classID) ----
  const model = useFormModel(
    () =>
      detail
        ? new FormModel({
            id: row.id,
            classID: config.classID,
            type: config.type,
            initialAttributes: { ...row, ...detail },
            validatedFields: config.validatedFields,
            // Dev-sync J4 — the UDA synchronize gate (component_model.js:
            // 155-227): a user_defined_accessory whose accumulated diff
            // carries ANY of the 12 air-condition keys while sync is on
            // POSTs its commit_air_conditions endpoint with the WHOLE diff
            // (payload: changes — :167), autoReload:false (:180). onCommit
            // merges the server's `updated` block over the local state
            // (savedState semantics, :185) and fires the section's
            // eadb/eawb event (:192-194). Legacy's onError has no revert —
            // returning the local changes as the echo matches that.
            saveFn: (changes) => {
              const syncOn = selectBoardSyncOn(store.getState());
              if (
                config.type === 'user_defined_accessory' &&
                syncOn &&
                hasAnyKey(changes, UDA_AIR_COMMIT_KEYS)
              ) {
                return guardSelect({
                  url: `/api/v1.0/ahu/user_defined_accessories/${row.id}/commit_air_conditions`,
                  payload: changes,
                  autoReload: false, // component_model.js:180
                  onCommit: () => {
                    const sid =
                      (row as { section_id?: number | null }).section_id ??
                      (detail as { section_id?: number | null } | undefined)?.section_id;
                    if (sid != null) {
                      ahuBoardBus.emit(`changed:section:${sid}:eadb_temp:eawb_temp`);
                    }
                  },
                }).then((resp) => ({
                  ...changes,
                  ...((resp?.updated as Record<string, unknown> | undefined) ?? {}),
                }));
              }
              return dispatch(
                ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
                  plural: config.plural,
                  id: row.id,
                  data: changes,
                })
              ).unwrap() as Promise<Record<string, unknown>>;
            },
            validation: {
              validateTunnelOnlyComponents: () => undefined,
              validateOnSectionChange: () => undefined,
              validateAccessoryOnChange: (componentId) =>
                void dispatch(validateAccessoryOnChange(componentId)),
            },
            viewRef: viewRef.current,
            // §49.2 success-path accessory extras (component_model.js:160-188).
            accessoryAfterSave: (changes) => {
              // hood w/h/tag PATCH success → select:hood:<id> (parity 304 —
              // the hood form's own listener runs the damper resync web).
              if (
                config.type === 'hood' &&
                (Object.prototype.hasOwnProperty.call(changes, 'width') ||
                  Object.prototype.hasOwnProperty.call(changes, 'height') ||
                  Object.prototype.hasOwnProperty.call(changes, 'tag'))
              ) {
                ahuBoardBus.emit(`select:hood:${row.id}` as `select:hood:${number}`);
              }
              const targets = viewRef.current.catalogueTargetFields;
              const changedKeys = Object.keys(changes);
              const masterChanged = targets.some((t) => changedKeys.includes(t));
              if (
                Object.prototype.hasOwnProperty.call(changes, 'height') ||
                Object.prototype.hasOwnProperty.call(changes, 'width') ||
                masterChanged
              ) {
                ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
                  tunnel_id: Number(section.tunnel_id),
                  hide_from_canvas: true,
                });
              }
              const pos = positionRef.current;
              if (
                fetchPositionFlag.current &&
                pos &&
                (changes.blade_direction_id ||
                  changes.master_damper_id ||
                  pos.get('default_pos_y') ||
                  pos.get('default_pos_x'))
              ) {
                fetchPositionFlag.current = false;
                void pos.fetch().then(() => bump());
                return;
              }
              fetchPositionFlag.current = false;
              bump();
            },
          })
        : null,
    [row.id, !detail]
  );
  const modelRef = useRef<FormModel | null>(null);
  modelRef.current = model;

  useEffect(() => {
    if (model) model.armAutoSave(); // forceSync for fresh rows, sync-arm otherwise
  }, [model]);

  // ---- position boot (§49.6:176-233 / parity 329) ----
  const [positionVersion, setPositionVersion] = useState(0);
  const attachPosition = useCallback(
    (pos: AccessoryPositionModel) => {
      positionRef.current?.destroy();
      positionRef.current = pos;
      pos.subscribe(() => {
        setPositionVersion((v) => v + 1);
        bump();
      });
      mountingRef.current?.setPosition(pos);
      setPositionVersion((v) => v + 1);
    },
    [bump]
  );

  useEffect(() => {
    if (!model) return undefined;
    if (!config.withPosition) {
      settlePosition();
      return undefined;
    }
    const positionId = model.get('position_id');
    if (config.loadPositionWhenAlreadySelected && !positionId) {
      settlePosition();
      return undefined;
    }
    // hood_id rows boot an on_cabinet model (§49.6:181) — none of the
    // 6e2b-1 types mounts on hoods, but the rule is cheap to keep.
    const base = model.get('hood_id') ? 'on_cabinet' : '';
    const pos = new AccessoryPositionModel({
      id: row.id,
      base: base as '' | 'on_cabinet',
      sectionId: Number(model.get('section_id') ?? section.id),
      dispatch,
    });
    attachPosition(pos);
    void pos.fetch().then(() => {
      // fan_intake catalogueFieldsIntake swap (§49.6:200-207) — damper-only.
      if (
        config.type === 'damper' &&
        config.catalogueFieldsIntake &&
        String(pos.get('mounting_plane') ?? '') === 'fan_intake'
      ) {
        setIntakeMode(true);
        setCmsURLOverride('/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/params/');
      }
      // access_door: stamp door_ids on the position (partner-door validation
      // sweep target — §49.6 render / parity 303).
      if (config.type === 'access_door' && model.get('unique')) {
        pos.doorIds =
          (model.get('door_ids') as Record<string, { section_id: number }> | null) ?? null;
      }
      bump();
      // `fetch()` swallows its own errors and resolves null, so this settles the
      // gate on failure too — the selector must open either way.
      settlePosition();
    });
    return () => pos.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, config.withPosition, config.loadPositionWhenAlreadySelected, row.id]);

  // ---- damper boot: mounted-on-hood detection (§49.6:239-247) ----
  useEffect(() => {
    if (config.type !== 'damper' || !model) return;
    const planeName = String(model.get('mounting_plane') ?? '');
    if (planeName.includes('Hood')) {
      markHoodIsMounting(true);
      // update:damper:params (model, hood_is_mounting=true) — own handler
      // (DamperForm web) rebuilds the hood-constrained CMS.
      ahuBoardBus.emit('update:damper:params', model.toJSON(), true, false);
    } else if (planeName) {
      markHoodIsMounting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type, model, model?.get('mounting_plane')]);

  // ---- mounting model (§51.4) ----
  useEffect(() => {
    if (!model || !config.setupMounting) return undefined;
    const mounting = new MountingModel({
      accessory: model,
      position: positionRef.current,
      getSectionDims: () => awDimsRef.current,
      getMountingPlanes: () => mountingPlanesRef.current,
    });
    mountingRef.current = mounting;
    const off = mounting.subscribe(bump);
    return () => {
      off();
      mounting.destroy();
      mountingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, config.setupMounting, mountingPlanes.length]);

  // ---- catalogue fields (§49.4 — shared engine) ----
  // 6e2b-2: damper on fan_intake swaps the WHOLE field set (initCatalogueFields
  // with catalogueFieldsIntake — §49.6:200-207); engines rebuild on swap.
  const effectiveCatalogueConfigs =
    intakeMode && config.catalogueFieldsIntake
      ? config.catalogueFieldsIntake
      : config.catalogueFields;
  viewRef.current.catalogueTargetFields = effectiveCatalogueConfigs.map((f) => f.targetField);
  const catalogueFields = useCatalogueFields({
    configs: effectiveCatalogueConfigs,
    model,
    dispatch,
    viewRef: viewRef.current,
  });

  const loading = !model || (!!config.cmsAccessoryURL && !cmsAccessory);

  // ---- subtabs (parity 308) + openGroup click-all on expand ----
  const [subtabs, setSubtabs] = useState<Record<string, boolean>>({});
  const toggleSubtab = useCallback((name: string, open: boolean) => {
    setSubtabs((prev) => ({ ...prev, [name]: open }));
  }, []);
  const wasExpanded = useRef(false);
  useEffect(() => {
    if (expanded && !wasExpanded.current) {
      if (suppressOpenGroupOnce.accessoryId === row.id) {
        // ADD-path programmatic expand (QA batch 8 #3c follow-up): legacy's
        // add rendered the row checked WITHOUT firing openGroup (api.js:413
        // trigger precedes the list render → tab.accessories.js:246
        // render-checked; :684 never ran) → child subtabs arrive COLLAPSED.
        suppressOpenGroupOnce.accessoryId = null;
      } else {
        // Manual expand — tab.accessories.js:684:
        // `input[data-subtab]:not(:checked) + label i`.click() on the WHOLE
        // match set: every labeled subtab opens.
        setSubtabs((prev) => {
          const next = { ...prev };
          args.labeledSubtabs.forEach((name) => {
            next[name] = true;
          });
          return next;
        });
      }
    }
    wasExpanded.current = expanded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // ---- changeSelectValue arming (§49.6 / parity 311) ----
  const autoSelectAllowed = useRef(false);
  const autoSelectAllowedField = useRef<string | null>(null);

  // ADD-path arming — the `data-new` read (accessory_view.js:372). Consumed
  // once, before the post-render scan below runs (effects fire in declaration
  // order, and that scan has no dep array so it runs on every render). The
  // markUserAction is legacy's `forceSync()` for a fresh row
  // (accessory_view.js:383): without it the userArmed data-safety gate blocks
  // both the catalogue auto-pick and the PATCH, and the defaults would be
  // in-memory only. Scoped to the row the user just created — every other
  // form stays inert on mount exactly as before.
  useEffect(() => {
    if (!model || !newlyAddedAccessoryIds.has(row.id)) return;
    newlyAddedAccessoryIds.delete(row.id);
    autoSelectAllowed.current = true;
    model.markUserAction();
    // Auto-open the selector dialog for a freshly created
    // opening/damper/hood/louver/uvc — the same `rating_mode:activate` a manual
    // label click emits (AccessorySelectHeading's ahuBoardBusEmitRating), built
    // the same way: `{id, ...aw dims}` sourced straight from awDimsRef the way
    // `getSectionDims` does, so there's no dependency on the form shim being
    // registered yet.
    //
    // The section ROW is spread in for uvc only, because legacy's auto-open IS
    // a click on that row's own label (tab.accessories.js:364) and must
    // therefore emit exactly what a manual click on it emits. UvcForm is the
    // one heading that passes a `section` row down (its selector's Project tab
    // reads section.name/.tag and the target-surface default reads
    // section.type); the other four pass none, so for them spreading it would
    // make the auto-open payload RICHER than their own manual click — a
    // divergence, not a fix.
    //
    // Creating several of these back-to-back re-fires this once per row, as
    // each one's own form mounts. ComponentSelectorDialog.renderFromAHU
    // replaces whatever dialog is currently open with the latest activation
    // (unless the open one has unsaved edits, in which case it defers behind
    // the save-changes prompt) — so with no extra coordination needed here,
    // the LAST accessory created is the one left open.
    if (AUTO_OPEN_SELECTOR_TYPES.has(config.type)) {
      ahuBoardBus.emit('rating_mode:activate', {
        name: config.type,
        current_model: { id: row.id, is_combo: !!model.get('is_combo') },
        section:
          config.type === 'uvc'
            ? { ...section, id: section.id, ...awDimsRef.current }
            : { id: section.id, ...awDimsRef.current },
      });
      ahuBoardBus.emit('open_rating_popup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, row.id]);

  // ---- prerender / refetch machinery (§49.6:483-576) ----
  const timers = useRef<number[]>([]);
  const addTimer = (id: number) => timers.current.push(id);
  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    },
    []
  );

  const refetchModel = useCallback(async (): Promise<void> => {
    const m = modelRef.current;
    if (!m) return;
    try {
      const fresh = await dispatch(
        ahuBoardApi.endpoints.getAccessoryDetail.initiate(
          { plural: config.plural, id: row.id },
          { subscribe: false, forceRefetch: true }
        )
      ).unwrap();
      m.set(fresh as Record<string, unknown>, { xhr: true });
      bump();
    } catch {
      /* fetch failure: keep current state (legacy fetch error path) */
    }
  }, [dispatch, config.plural, row.id, bump]);

  const casingChanged = useCallback(() => {
    // §49.6 casingChanged (:544-576): auto-position types with default_pos →
    // position refetch; damper/opening/hood/louver on plane 2/3 →
    // reCheckMountingCentering (result computed but NOT saved — the legacy
    // position.save is commented out; on trigger: fetch + render + validate).
    const m = modelRef.current;
    const pos = positionRef.current;
    if (
      config.hasAutoPosition &&
      pos &&
      (pos.get('default_pos_y') || pos.get('default_pos_x'))
    ) {
      void pos.fetch().then(() => bump());
      return;
    }
    const recheck =
      config.type === 'damper' ||
      config.type === 'opening' ||
      config.type === 'hood' ||
      config.type === 'louver';
    const planeId = pos ? Number(pos.get('mounting_plane_id')) : null;
    if (!m || !pos || !recheck || (planeId !== 2 && planeId !== 3)) {
      bump();
      return;
    }
    // reCheckMountingCentering (§49.6:577-599) — label-gated, |dist−pos|>0.001
    // midpoint math; result triggers fetch+render+validate, never a write.
    const json = m.toJSON();
    const label = String(json.label ?? '');
    const autoCenterX = pos.get('auto_center_x');
    const autoCenterY = pos.get('auto_center_y');
    let triggered = false;
    if (
      (label.startsWith('Damper') ||
        label.startsWith('Louver') ||
        label.startsWith('Opening') ||
        label.startsWith('Hood')) &&
      (autoCenterX || autoCenterY)
    ) {
      const posX = Number(pos.get('x'));
      const posY = Number(pos.get('y'));
      const x = Number(json.distance_to_downstream_wall || json.distance_to_right_wall);
      const y = Number(json.distance_to_roof || json.distance_to_right_wall);
      const sumX = x + posX;
      const sumY = y + posY;
      if (
        (sumX && autoCenterX && Math.abs(x - posX) > 0.001 && Number((sumX / 2).toFixed(3)) > 0) ||
        (sumY && autoCenterY && Math.abs(y - posY) > 0.001 && Number((sumY / 2).toFixed(3)) > 0)
      ) {
        triggered = true;
      }
    }
    if (triggered) {
      const doorIds = m.get('door_ids') as Record<string, { section_id: number }> | null;
      const connectedSections = new Set<number>();
      if (doorIds) Object.values(doorIds).forEach((el) => connectedSections.add(Number(el.section_id)));
      void refetchModel().then(() => {
        void dispatch(validateAccessoryOnChange(row.id));
        connectedSections.forEach((sid) => void dispatch(validateSectionAccessories(sid)));
      });
    } else bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.hasAutoPosition, config.type, bump, row.id, dispatch]);

  const prerenderTimer = useRef<number | null>(null);
  const prerender = useCallback(
    (data?: Record<string, unknown> | null, casing?: boolean, change?: Record<string, unknown>) => {
      const m = modelRef.current;
      if (!m) return;
      if (
        (data?.only_for && data.only_for != m.get('section_id')) ||
        (data?.tunnel_id && data.tunnel_id !== m.get('tunnel_id') && data.tunnel_id !== section.tunnel_id) ||
        data?.accessories_fetch === false
      ) {
        return;
      }
      if (change?.update_position) {
        if (prerenderTimer.current) window.clearTimeout(prerenderTimer.current);
        prerenderTimer.current = window.setTimeout(() => {
          const pos = positionRef.current;
          if (pos?.get('default_pos_x')) {
            // legacy re-asserts default_pos_x then refetches (§49.6:492-499)
            // — a user-driven cabinet-edit cascade, never a mount-time write.
            void pos.save({ default_pos_x: true }).then(() => void refetchModel());
            return;
          }
          void refetchModel();
        }, 500);
        return;
      }
      if (prerenderTimer.current) window.clearTimeout(prerenderTimer.current);
      prerenderTimer.current = window.setTimeout(() => {
        void refetchModel().then(() => {
          if (casing) casingChanged();
        });
      }, 200);
    },
    [refetchModel, casingChanged, section.tunnel_id]
  );

  // ---- selectHood (§49.6:2158-2210 / parity 335) — hood-only actor ----
  // For every damper in the hood's section: refetch damper + on_cabinet
  // position JSON and broadcast the resync web (update:damper:params/count,
  // render:damper:mounting, update:damper:<id>).
  const selectHood = useCallback(() => {
    const m = modelRef.current;
    if (!m || config.type !== 'hood') return;
    const sectionId = Number(m.get('section_id') ?? section.id);
    void dispatch(
      ahuBoardApi.endpoints.getSectionAccessories.initiate(sectionId, {
        subscribe: false,
        forceRefetch: true,
      })
    )
      .unwrap()
      .then((rows) => {
        (rows as Array<Record<string, unknown>>)
          .filter((r) => String(r.type ?? '') === 'damper')
          .forEach((r) => {
            const damperId = Number(r.id);
            void dispatch(
              ahuBoardApi.endpoints.getAccessoryDetail.initiate(
                { plural: 'dampers', id: damperId },
                { subscribe: false, forceRefetch: true }
              )
            )
              .unwrap()
              .then((damperJson) => {
                ahuBoardBus.emit(
                  'update:damper:params',
                  damperJson as Record<string, unknown>,
                  true,
                  true
                );
                ahuBoardBus.emit('update:damper:count', damperJson as Record<string, unknown>);
                void dispatch(
                  accessoryFormsApi.endpoints.getPositionByBase.initiate(
                    { base: 'on_cabinet', id: damperId },
                    { subscribe: false, forceRefetch: true }
                  )
                )
                  .unwrap()
                  .then((posJson) => {
                    ahuBoardBus.emit(
                      'render:damper:mounting',
                      posJson as Record<string, unknown>,
                      undefined,
                      undefined
                    );
                    ahuBoardBus.emit(
                      `update:damper:${damperId}` as `update:damper:${number}`,
                      damperJson,
                      posJson
                    );
                  });
              })
              .catch(() => undefined);
          });
      })
      .catch(() => undefined);
  }, [config.type, dispatch, section.id]);

  // ---- dispatcher listeners (§49.6 ctor) ----
  useEffect(() => {
    if (!model) return undefined;
    const offs: Array<() => void> = [];

    if (config.setupMounting) {
      offs.push(
        ahuBoardBus.on('change:tunnel:dimensions', (data) => prerender(data as Record<string, unknown>, true))
      );
      offs.push(
        ahuBoardBus.on('change:tunnel:casing', (data) => prerender(data as Record<string, unknown> | undefined, true))
      );
      offs.push(
        ahuBoardBus.on(`remove_accessory_esp_${row.id}` as `remove_accessory_esp_${number}`, () => {
          // recalc (§49.6:536-543).
          if (model.get('apd')) {
            ahuBoardBus.emit('fetch:fans:esp', {
              air_stream_ids: [Number(model.get('air_stream_id'))],
            });
          }
        })
      );
      // select:hood:<id> — hood w/h/tag PATCH success → damper resync web
      // (registered for every setupMounting type in legacy; only hood acts).
      offs.push(
        ahuBoardBus.on(`select:hood:${row.id}` as `select:hood:${number}`, () => selectHood())
      );
    }
    if (config.hasAutoPosition) {
      offs.push(
        ahuBoardBus.on(
          `change:casing:section_${Number(model.get('section_id') ?? section.id)}` as `change:casing:section_${number}`,
          (...busArgs: unknown[]) => prerender(null, true, busArgs[0] as Record<string, unknown>)
        )
      );
    }
    if (config.type === 'drain_pan') {
      offs.push(
        ahuBoardBus.on(
          `section:change:qty:${section.id}` as `section:change:qty:${number}`,
          () => {
            addTimer(window.setTimeout(() => prerender(), 1500));
          }
        )
      );
      offs.push(
        ahuBoardBus.on(
          `change:tunnel:widthOrHeight:${Number(section.tunnel_id)}` as `change:tunnel:widthOrHeight:${number}`,
          () => {
            addTimer(window.setTimeout(() => void refetchModel(), 900));
          }
        )
      );
    }
    // ALL types listen changeDrainPan (§49.6:413-419).
    offs.push(
      ahuBoardBus.on('changeDrainPan', () => {
        addTimer(window.setTimeout(() => void refetchModel(), 900));
      })
    );
    // update:damper:<id> silent set + redraw (§49.6:430-434) — damper-only
    // sender, harmless generic wire (6e2b-2 exercises it).
    offs.push(
      ahuBoardBus.on(`update:damper:${row.id}` as `update:damper:${number}`, (...busArgs: unknown[]) => {
        const [m, p] = busArgs as [Record<string, unknown>, Record<string, unknown>];
        if (m) model.set(m, { silent: true });
        if (p && positionRef.current) positionRef.current.set(p, { silent: true });
        bump();
      })
    );
    // deselct:model:<id> [sic] → re-arm changeSelectValue (§49.6:405-411).
    offs.push(
      ahuBoardBus.on(`deselct:model:${row.id}` as `deselct:model:${number}`, (...busArgs: unknown[]) => {
        autoSelectAllowed.current = true;
        autoSelectAllowedField.current = String(busArgs[0] ?? '');
      })
    );
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, row.id, section.id, config.type]);

  // ---- socket listeners (§49.6:435-456 / parity 337) ----
  useEffect(() => {
    if (!model) return undefined;
    let drainTimer: number | null = null;
    let posTimer: number | null = null;
    const handler = (msg: SocketDataMessage) => {
      if (msg.id != row.id) return;
      if (msg.code === 'put' && msg.model === 'ahuaccessorydrainpan') {
        if (msg.data && model.get('dim1_length') == msg.data.dim1_length) return;
        if (drainTimer) window.clearTimeout(drainTimer);
        drainTimer = window.setTimeout(() => {
          document.querySelectorAll('.added-drain-pan-note').forEach((el) => el.classList.remove('active'));
          if (msg.data) model.set(msg.data, { __from_back_end: true });
          bump();
        }, 1000);
      }
      if (positionRef.current && msg.model === 'positioningoncabinet') {
        if (posTimer) window.clearTimeout(posTimer);
        posTimer = window.setTimeout(() => {
          const pos = positionRef.current;
          if (!pos) return;
          pos.no_fetch = true;
          pos.set((msg.data ?? {}) as Record<string, unknown>);
          bump();
        }, 100);
      }
    };
    const socket = getSocket();
    socket.on('data', handler);
    return () => {
      socket.off('data', handler);
      if (drainTimer) window.clearTimeout(drainTimer);
      if (posTimer) window.clearTimeout(posTimer);
    };
  }, [model, row.id, bump]);

  // ---- "adding drain pan…" note teardown (§49.6:833-838) ----
  // accessory_view.js:833-838, inside render(), guarded by `!already_rendered`:
  // the FIRST paint of a drain-pan accessory view clears the note the coil
  // selector raised on Select (CoilSelector `finishSelectCoil`) 2 s later.
  //
  // This is the note's normal exit — the socket-`put` path above only fires for
  // a drain pan whose `dim1_length` the backend later revises, which is not
  // guaranteed, and never for one this form has only just mounted for. Without
  // it the note is stuck up permanently once raised.
  const drainPanNoteCleared = useRef(false);
  useEffect(() => {
    if (config.type !== 'drain_pan' || loading || drainPanNoteCleared.current) return undefined;
    drainPanNoteCleared.current = true;
    if (!document.querySelector('.added-drain-pan-note.active')) return undefined;
    const timer = window.setTimeout(() => {
      document
        .querySelectorAll('.added-drain-pan-note.active')
        .forEach((el) => el.classList.remove('active'));
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [config.type, loading]);

  // ---- drawOnCanvas (§49.6:2032-2116 / parity 334) ----
  const canvasSnapshot = useRef<string | null>(null);
  const isAlreadyRendered = useRef(false);
  const firstDrawSeeded = useRef(false);
  useEffect(() => {
    const m = modelRef.current;
    if (!m || loading) return;
    if (config.type === 'drain_pan') {
      const { canvasData, gatePassed } = buildDrainPanCanvasData(m.toJSON());
      const json = JSON.stringify(canvasData);
      if (!firstDrawSeeded.current) {
        firstDrawSeeded.current = true;
        canvasSnapshot.current = json;
        // boot feed (6c2 bootDrawFeeds) already drew it — suppress the
        // duplicate mount emit; legacy net visuals identical.
        isAlreadyRendered.current = gatePassed;
        return;
      }
      if (isAlreadyRendered.current && !canvasData.dim1_length) isAlreadyRendered.current = false;
      if (json != canvasSnapshot.current && gatePassed) {
        ahuBoardBus.emit(
          'draw:accessory:on:canvas',
          m.toJSON(),
          config.type,
          { id: Number(m.get('section_id') ?? section.id) },
          {},
          isAlreadyRendered.current
        );
        isAlreadyRendered.current = true;
      }
      canvasSnapshot.current = json;
      return;
    }
    const pos = positionRef.current;
    if (!pos) return;
    const built = buildAccessoryCanvasData({
      model: m.toJSON(),
      position: pos.toJSON(),
      type: config.type,
      cmsAccessory,
      internalWalls: internalWallsRef.current, // plane>6 IW lookup (parity 334)
    });
    const json = JSON.stringify(built.canvasData);
    const prev = canvasSnapshot.current;
    const gate = accessoryDrawGate(config.type, built, json, prev, isAlreadyRendered.current, m.toJSON());
    if (!firstDrawSeeded.current) {
      firstDrawSeeded.current = true;
      canvasSnapshot.current = json;
      isAlreadyRendered.current = gate.draw; // boot feed drew it if the gate passes
      return;
    }
    if (gate.remove) {
      ahuBoardBus.emit('remove_accessory_from_canvas', row.id);
      canvasSnapshot.current = json;
      isAlreadyRendered.current = false;
      return;
    }
    if (gate.resetRendered) isAlreadyRendered.current = false;
    if (gate.draw) {
      ahuBoardBus.emit(
        'draw:accessory:on:canvas',
        m.toJSON(),
        built.drawLabel,
        { id: Number(m.get('section_id') ?? section.id) },
        pos.toJSON(),
        isAlreadyRendered.current,
        built.viewport,
        built.intWall
      );
      isAlreadyRendered.current = true;
    }
    canvasSnapshot.current = json;
  });

  // ---- infoForEsp diff → fetch:fans:esp (§49.6:828-839 + :1449-1461 /
  // parity 317-318): uda/opening/louver/damper/hood re-fire the fan ESP
  // fetch when apd or air_stream change (6e2b-1 exercises uda; the rest
  // arrive with 6e2b-2). JSON-diff gated like the legacy infoForEsp cache.
  const infoForEsp = useRef<{ apd: unknown; air_stream_id: unknown } | null>(null);
  useEffect(() => {
    const m = modelRef.current;
    if (!m) return;
    if (!ESP_INFO_TYPES.has(config.type)) return;
    const apd = m.get('apd');
    const airStream = m.get('air_stream_id');
    const prev = infoForEsp.current;
    if (prev && (prev.apd != apd || prev.air_stream_id != airStream)) {
      ahuBoardBus.emit('fetch:fans:esp', {
        air_stream_ids: [Number(airStream), Number(prev.air_stream_id)].filter((v) =>
          Number.isFinite(v)
        ),
      });
    }
    infoForEsp.current = { apd, air_stream_id: airStream };
  });

  // ---- shouldFetchFanIsp (§49.6:2117-2131 / parity 317): fan-section
  // damper/louver/hood/opening size/master/plane deltas → refetch fan ISP ----
  const ispSnapshot = useRef<string | null>(null);
  useEffect(() => {
    const m = modelRef.current;
    if (!m) return;
    if (!ESP_INFO_TYPES.has(config.type) || config.type === 'user_defined_accessory') return;
    if (String((section as Record<string, unknown>).type ?? '') !== 'fan') return;
    const snap = JSON.stringify({
      height: m.get('height'),
      height_2: m.get('height_2'),
      width: m.get('width'),
      apd: m.get('apd'),
      master: m.get(`master_${config.type}_id`),
      mounting_plane: positionRef.current?.get('mounting_plane_id') ?? null,
    });
    if (ispSnapshot.current !== null && ispSnapshot.current !== snap) {
      ahuBoardBus.emit(
        `refetch_fan_${Number(m.get('section_id') ?? section.id)}_isp` as `refetch_fan_${number}_isp`
      );
    }
    ispSnapshot.current = snap;
  });

  // ---- access_door partner-door dedup (§49.6:387-396 / parity 329):
  // the non-`unique` door of a shared-wall pair removes its own row LI ----
  useEffect(() => {
    if (config.type !== 'access_door' || !model) return;
    if (model.get('unique') === false) {
      const li = formRef.current?.closest('li.accessory');
      if (li) (li as HTMLElement).classList.add('hide');
    }
  }, [config.type, model]);

  // ---- autoSize engine (§49.6 onAutoSze + render attempt / parity 331) ----
  const autoSizeRender = useRef(0);
  const runAutoSize = useCallback(
    (force: boolean) => {
      const m = modelRef.current;
      if (!m || !args.autoSize) return;
      if (force) m.markUserAction();
      const data: Record<string, unknown> = { airflow_actual: m.get('airflow_actual') };
      const changed = args.autoSize(
        force,
        m,
        data,
        desiredFaceVelocityRef.current,
        cmsAccessoryRef.current
      );
      if (changed) {
        delete data.airflow_actual;
        m.set(data);
        // snapshot → auto_size_disabled until inputs change (§49.6 render).
        const snap: Record<string, unknown> = {
          desired_face_velocity_actual: desiredFaceVelocityRef.current,
          airflow_actual: m.get('airflow_actual'),
          ...(args.autoSizeGetState?.(m.toJSON()) ?? {}),
        };
        autoSizeSnapshot.current = snap;
        setAutoSizeDisabled(true);
        bump();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bump]
  );
  // render-time attempt (legacy: every render after the first when airflow is
  // set — fills empty dims; the userArmed gate keeps it in-memory until the
  // user touches the form, so mount never PATCHes).
  // 5e-sync — dev 5d46356d4f (accessory_view.js:770): the AUTO-triggered
  // attempt is skipped under view-only permission (the explicit button-forced
  // runAutoSize(true) path stays, like legacy).
  useEffect(() => {
    const m = modelRef.current;
    if (!m || loading || !args.autoSize) return;
    autoSizeRender.current += 1;
    if (autoSizeRender.current === 1) return; // legacy skips the first render
    if (document.body.classList.contains('ahu-view-permission')) return;
    if (m.get('airflow_actual')) runAutoSize(false);
  });
  // auto_size_disabled release: any snapshot input drift re-enables (§49.6).
  useEffect(() => {
    const m = modelRef.current;
    if (!m || !autoSizeSnapshot.current) return;
    const snap = autoSizeSnapshot.current;
    const sizeState = args.autoSizeGetState?.(m.toJSON()) ?? {};
    const same =
      String(desiredFaceVelocity) === String(snap.desired_face_velocity_actual) &&
      String(m.get('airflow_actual') ?? '') === String(snap.airflow_actual ?? '') &&
      Object.entries(sizeState).every(([k, v]) => String(v ?? '') === String(snap[k] ?? ''));
    if (autoSizeDisabled !== same) setAutoSizeDisabled(same);
  });
  const setDesiredFaceVelocity = useCallback(
    (value: string) => {
      writeDesiredFaceVelocity(row.id, value);
      setDesiredFaceVelocityState(value);
    },
    [row.id]
  );

  // ---- hood sole-mtl auto-set (§49.6:707-715 / parity 330): exactly one CMS
  // material + mtl_id null → auto-pick it (in-memory until userArmed). ----
  useEffect(() => {
    const m = modelRef.current;
    if (config.type !== 'hood' || !m || !cmsAccessory) return;
    const mtl = cmsAccessory.mtl as Array<Record<string, unknown>> | undefined;
    if (mtl && mtl.length === 1 && m.get('mtl_id') == null) {
      m.set({ mtl_id: mtl[0].id });
    }
  }, [config.type, cmsAccessory, model]);

  // ---- changeSelectValue accessory variant (§49.6:1086-1135 / parity 311) ----
  useEffect(() => {
    const m = modelRef.current;
    const root = formRef.current;
    if (!m || !root || loading) return;
    const forEmpty = !autoSelectAllowed.current;
    if (forEmpty) return; // armed only by data-new / deselct:model — user paths
    autoSelectAllowed.current = false;
    const paramsSourceFields = config.catalogueFields.map((f) => f.paramsSourceField);
    const autoSelect: Record<string, Record<string, unknown>> = {};
    paramsSourceFields.forEach((f) => {
      autoSelect[f] = {};
    });
    const modelField: Record<string, unknown> = {};
    const fieldExist: Record<string, boolean> = {};
    let change = false;
    // root IS the <form> since the verifier ref move — scan its selects directly.
    root.querySelectorAll('select').forEach((el) => {
      const select = el as HTMLSelectElement;
      if ((select.value === 'None' || !select.value) && !select.disabled && !select.classList.contains('non-clickable')) {
        const field = Array.from(select.classList)
          .map((c) => c.replace('_field', ''))
          .map((c) => paramsSourceFields.find((f) => f === `${c}_params` || f === c))
          .find((f) => f);
        const json = m.toJSON();
        const fieldJson = field && json[field] ? (JSON.parse(String(json[field])) as Record<string, unknown>) : null;
        const value = fieldJson ? fieldJson[select.name] : null;
        const options = Array.from(select.options).filter((o) => o.value !== 'None');
        const option = value != null ? options.find((o) => o.value == String(value)) : null;
        if (value != null && options.length && !option && field) {
          const catalogue = Object.values(catalogueFields).find(
            (e) => e.config.paramsSourceField === field
          );
          m.set({ [field]: JSON.stringify({}) });
          void catalogue?.checkIfThereIsOnlyOneFoundModel(true);
        } else if (options.length === 1) {
          if (field) {
            autoSelect[field][select.name] = options[0].value;
            change = true;
            fieldExist[field] = true;
            if (select.classList.contains('model_field')) modelField[select.name] = options[0].value;
          } else if (select.classList.contains('model_field')) {
            modelField[select.name] = options[0].value;
            change = true;
          }
        }
      }
    });
    if (change) {
      const forSave: Record<string, unknown> = { ...modelField };
      Object.keys(autoSelect).forEach((f) => {
        if (fieldExist[f] && (!autoSelectAllowedField.current || autoSelectAllowedField.current === f)) {
          const oldData = (() => {
            try {
              return (JSON.parse(String(m.get(f) ?? '{}')) as Record<string, unknown>) || {};
            } catch {
              return {};
            }
          })();
          forSave[f] = JSON.stringify({ ...oldData, ...autoSelect[f] });
        }
      });
      autoSelectAllowedField.current = null;
      // only_viewport pairing — accessory changeSelectValue variant
      // (accessory_view.js:1132 / parity 311): a viewport_params auto-commit
      // also stamps only_viewport with the current master_viewport_id.
      if (forSave.viewport_params) forSave.only_viewport = m.get('master_viewport_id');
      m.set(forSave);
    }
  });

  // ---- toolbar tag rewrite (§49.6 changeTagOnBoard / parity 339) ----
  // (legacy also calls ahu.validateOnAccessoriesTagChange — a pure error-log
  // label rewrite-in-place; the React ErrorManager re-labels on its next
  // validation render. Documented deviation, no HTTP either way.)
  const changeTagOnBoard = useCallback((tag: string, id: number) => {
    ahuBoardBus.emit('update:accessories:tag:onboard', { tag, id });
    const label = document.querySelector(`#forms_accessory_${id} > label`);
    const info = label?.textContent ?? '';
    document
      .querySelectorAll<HTMLElement>('#canvas_toolbar #accessories-ul span.accessory_info')
      .forEach((el) => {
        el.innerHTML = `: ${info}`;
      });
  }, []);

  // ---- field commits (§49.6 onModelFieldChange) ----
  const commitModelField = useCallback(
    (
      name: string,
      raw: string | null,
      opts?: { min?: number; max?: number; formatter?: 'decimal' | 'integer' }
    ) => {
      const m = modelRef.current;
      if (!m) return;
      m.markUserAction();
      if (positionRef.current) positionRef.current.no_fetch = false;
      let value: unknown = raw;
      if (raw === null || raw === '' || raw === 'None') value = null;
      else if (opts?.formatter) {
        // validatedNumberFields clamp (§49.6:1209-1223).
        const res = validateNumberField(raw, {
          min: opts.min ?? null,
          max: opts.max ?? null,
          mode: 'clamp',
          step: opts.formatter === 'integer' ? 1 : 0.01,
        });
        value = res.value;
      } else if (name.endsWith('_id')) value = selectValueToId(raw);
      const data: Record<string, unknown> = { [name]: value };
      // drain_pan dim1_length carries the BE `select:false` flag (§51.2).
      if (config.type === 'drain_pan' && name === 'dim1_length') data.select = false;
      // Dev-sync J7 — uda_season auto-sync on type change (accessory_view.js
      // :1381-1391, one merged set → ONE PATCH like legacy): electric heater
      // (3) and humidifier (4) both force 'winter' (the redundant branches
      // are legacy's own); any other type nulls the season. This is also the
      // persisted half of the electric-heater self-heal (see UdaForm).
      if (config.type === 'user_defined_accessory' && name === 'uda_name_id') {
        if (value === 3) {
          data.uda_season = 'winter';
        } else if (value === 4) {
          data.uda_season = 'winter';
        } else {
          data.uda_season = null;
        }
      }
      // dog_house floor gauge null-cascade (§49.6:1343-1346).
      if (config.type === 'dog_house' && name === 'floor_mtl_gauge_id' && !value) {
        data.floor_under_insulation_id = null;
        data.floor_under_liner_mtl_gauge_id = null;
      }
      // opening: every non-{tag,air_stream,airflow} edit greys ALL sublists
      // until the PATCH settles (§49.6:1232-1234 / parity 330).
      if (
        config.type === 'opening' &&
        name !== 'tag' &&
        name !== 'air_stream_id' &&
        name !== 'airflow_actual'
      ) {
        setSublistsGreyed(true);
      }
      // hood style change: clear height_2 unless 4/5-sided + re-run autoSize
      // with the pending style (§49.6:1347-1360 / parity 331).
      if (config.type === 'hood' && name === 'style_id') {
        if (value != 4 && value != 5) data.height_2 = null;
        const airflow = m.get('airflow_actual');
        if (airflow && args.autoSize) {
          const autoData: Record<string, unknown> = { ...data, airflow_actual: airflow };
          args.autoSize(false, m, autoData, desiredFaceVelocityRef.current, cmsAccessoryRef.current);
          delete autoData.airflow_actual;
          Object.assign(data, autoData);
        }
      }
      // hood style-4 height edit auto-derives height_2 = round(h·2/3) (§49.6).
      if (config.type === 'hood' && name === 'height' && m.get('style_id') == 4 && value) {
        data.height_2 = Math.round((Number(value) * 2) / 3);
      }
      // enc_width/enc_height: the VFD enclosure dims — in Backbone these
      // inputs are DOM-named width/height (vfd form.tmpl :165/:169) so they
      // hit this same fetchPosition path; enc dims drive the position
      // distance calcs (vfd_viewset.py w_dim/h_dim).
      if (name === 'height' || name === 'width' || name === 'enc_height' || name === 'enc_width') {
        fetchPositionFlag.current = true;
      }
      if (name === 'tag') changeTagOnBoard(String(raw ?? ''), row.id);
      if (name === 'uda_name') changeTagOnBoard(String(raw ?? ''), row.id);
      m.set(data);
      // damper blade_direction while hood-mounted: save → rebuild damper CMS
      // + position refetch + count (§49.6:1362-1382 / parity 335).
      if (name === 'blade_direction_id') {
        fetchPositionFlag.current = true;
        if (config.type === 'damper' && m.get('hood_id')) {
          void m.syncProcess.then(() => {
            const mm = modelRef.current;
            if (!mm) return;
            ahuBoardBus.emit('update:damper:params', mm.toJSON(), true, false);
            void dispatch(
              accessoryFormsApi.endpoints.getPositionByBase.initiate(
                { base: 'on_cabinet', id: row.id },
                { subscribe: false, forceRefetch: true }
              )
            )
              .unwrap()
              .then(() => ahuBoardBus.emit('update:damper:count', mm.toJSON()))
              .catch(() => undefined);
          });
        }
      }
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.type, row.id, changeTagOnBoard, bump, dispatch]
  );

  const commitModelCheckbox = useCallback(
    (name: string, checked: boolean) => {
      const m = modelRef.current;
      if (!m) return;
      m.markUserAction();
      if (positionRef.current) positionRef.current.no_fetch = false;
      m.set({ [name]: checked });
      bump();
    },
    [bump]
  );

  const commitParamsField = useCallback(
    (targetField: string, name: string, raw: string) => {
      const engine = catalogueFields[targetField];
      const m = modelRef.current;
      if (!engine || !m) return;
      m.markUserAction();
      engine.updateFilter({ [name]: raw === 'None' ? null : raw });
    },
    [catalogueFields]
  );

  // ---- createPosition (§49.6:1793-1953 / parity 332, shim #10) ----
  const createPosition = useCallback(
    async (
      positionType: string,
      mountingPlaneId: number | string,
      mountingValue?: unknown,
      changeTagName?: string,
      extraData: Record<string, unknown> = {}
    ): Promise<AccessoryPositionModel | undefined> => {
      const m = modelRef.current;
      if (!m) return undefined;
      m.markUserAction();
      // ---- hood MOUNT transition (§49.6:1797-1868 / parity 332, 335) ----
      // damper picked a Hood entry from the mounting list: hood_id saved on
      // the damper, position becomes the hood-tracking on_cabinet row, the
      // damper CMS rebuilds hood-constrained (update:damper:* web).
      if (mountingValue === 'hood') {
        try {
          // legacy `model.save({hood_id})` (full PUT); ported as PATCH —
          // identical end state, only hood_id changes.
          const saved = await dispatch(
            ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
              plural: config.plural,
              id: row.id,
              data: { hood_id: Number(mountingPlaneId) },
            })
          ).unwrap();
          m.set(saved as Record<string, unknown>, { xhr: true });
          const posJson = await dispatch(
            accessoryFormsApi.endpoints.getPositionByBase.initiate(
              { base: 'on_cabinet', id: row.id },
              { subscribe: false, forceRefetch: true }
            )
          ).unwrap();
          m.set({ position_id: row.id, ...extraData });
          const pos = new AccessoryPositionModel({
            id: row.id,
            base: 'on_cabinet',
            sectionId: Number(m.get('section_id') ?? section.id),
            dispatch,
            initialAttributes: { ...(posJson as Record<string, unknown>), ...extraData },
          });
          pos.markUserAction();
          markHoodIsMounting(true);
          ahuBoardBus.emit('update:damper:params', m.toJSON(), true, false);
          ahuBoardBus.emit('update:damper:count', m.toJSON());
          ahuBoardBus.emit('update:damper:fields', m.toJSON());
          attachPosition(pos);
          return pos;
        } catch {
          return undefined;
        }
      }
      // ---- hood UN-MOUNT transition (§49.6:1869-1903) ----
      if (hoodIsMountingRef.current) {
        const base2 = POSITION_BASE_FOR_TYPE[positionType] ?? 'on_cabinet';
        const planeId2 = parseInt(String(mountingPlaneId), 10);
        const pos = new AccessoryPositionModel({
          id: row.id,
          base: base2,
          sectionId: Number(m.get('section_id') ?? section.id),
          dispatch,
          initialAttributes: { id: row.id, mounting_plane_id: planeId2, ...extraData },
        });
        pos.markUserAction();
        try {
          await dispatch(
            ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
              plural: config.plural,
              id: row.id,
              data: { position_id: row.id, hood_id: null },
            })
          ).unwrap();
          const savedPos = await dispatch(
            accessoryFormsApi.endpoints.putPositionByBase.initiate({
              base: base2,
              id: row.id,
              data: { mounting_plane_id: planeId2, ...extraData },
            })
          ).unwrap();
          pos.set(savedPos as Record<string, unknown>, { xhr: true });
        } catch {
          /* legacy: no rollback */
        }
        markHoodIsMounting(false);
        attachPosition(pos);
        ahuBoardBus.emit('update:damper:fields', m.toJSON());
        void refetchModel().then(() => {
          const mm = modelRef.current;
          if (!mm) return;
          ahuBoardBus.emit('update:damper:count', mm.toJSON());
          ahuBoardBus.emit('update:damper:params', mm.toJSON(), false, false);
        });
        return pos;
      }
      const base = POSITION_BASE_FOR_TYPE[positionType] ?? 'on_cabinet';
      const planeId =
        POSITION_BASE_FOR_TYPE[positionType] != null
          ? parseInt(String(mountingPlaneId), 10)
          : null; // unknown type → on_cabinet with null plane (§49.6:1904-1910)
      const pos = new AccessoryPositionModel({
        id: Number(m.get('id') ?? row.id),
        base,
        sectionId: Number(m.get('section_id') ?? section.id),
        dispatch,
        initialAttributes: { id: row.id, mounting_plane_id: planeId, ...extraData },
      });
      pos.markUserAction();
      try {
        const saved = await dispatch(
          accessoryFormsApi.endpoints.putPositionByBase.initiate({
            base,
            id: row.id,
            data: { mounting_plane_id: planeId, ...extraData },
          })
        ).unwrap();
        pos.set(saved as Record<string, unknown>, { xhr: true });
      } catch {
        /* legacy save error path: keep going (no rollback) */
      }
      // validation split (§49.6:1928-1939).
      if (changeTagName === 'mounting_plane_id') {
        void dispatch(validateSectionAccessories(Number(m.get('section_id') ?? section.id)));
        const doorIds = m.get('door_ids') as Record<string, { section_id: number }> | null;
        if (doorIds) {
          Object.values(doorIds).forEach(({ section_id }) =>
            dispatch(validateSectionAccessories(Number(section_id)))
          );
        }
      } else {
        void dispatch(validateAccessoryOnChange(Number(m.get('id') ?? row.id)));
        const doorIds = m.get('door_ids') as Record<string, { section_id: number }> | null;
        if (doorIds) {
          Object.keys(doorIds).forEach((id) => dispatch(validateAccessoryOnChange(Number(id))));
        }
      }
      m.set({ position_id: pos.id });
      attachPosition(pos);
      return pos;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, row.id, section.id, attachPosition, config.plural, markHoodIsMounting, refetchModel]
  );

  // ---- position field commits (§49.6 onPositionFieldChange) ----
  const commitPositionField = useCallback(
    (name: string, raw: string | null, opts?: { positionOnly?: boolean }) => {
      const m = modelRef.current;
      const pos = positionRef.current;
      if (!m) return;
      // `positionOnly` arms the POSITION model but not the accessory model
      // (accessDoorFloorClearance's To Floor correction — an automatic write
      // with no user gesture behind it, which must not unlock the accessory's
      // own programmatic saves). A real field edit arms both, as legacy's
      // onPositionFieldChange forceSync does.
      if (!opts?.positionOnly) m.markUserAction();
      if (pos) {
        pos.no_fetch = false;
        pos.markUserAction();
      }
      if (!m.get('position_id') || !pos) return; // plane select creates first
      let key = name;
      if (/position_./.test(name)) key = name[name.length - 1];
      pos.set({ [key]: raw == null || raw === '' ? null : raw });
      addTimer(
        window.setTimeout(() => {
          void refetchModel();
        }, 500)
      );
    },
    [refetchModel]
  );

  const commitPositionCheckbox = useCallback(
    (name: string, checked: boolean) => {
      const m = modelRef.current;
      const pos = positionRef.current;
      if (!m || !pos) return;
      m.markUserAction();
      pos.no_fetch = false;
      pos.markUserAction();
      pos.set({ [name]: checked });
      // default_pos un-check renders immediately; re-check refetches (§49.6:1642-1652).
      if ((name === 'default_pos_y' || name === 'default_pos_x') && !checked) {
        bump();
        return;
      }
      addTimer(
        window.setTimeout(() => {
          void refetchModel();
        }, 500)
      );
    },
    [refetchModel, bump]
  );

  const commitMountingPlane = useCallback(
    (raw: string, positionType: string | undefined, mountingValue?: string) => {
      const m = modelRef.current;
      if (!m) return;
      m.markUserAction();
      const pos = positionRef.current;
      if (!m.get('position_id') || !pos) {
        void createPosition(positionType ?? 'on_cabinet', raw, mountingValue, undefined).then(() =>
          bump()
        );
        return;
      }
      pos.markUserAction();
      // mounting_type switch check (§49.6:1664-1684): both branches call
      // createPosition (legacy re-PUTs the position with the new plane);
      // mountingValue routes the damper hood mount/unmount transitions.
      void createPosition(positionType ?? 'on_cabinet', raw, mountingValue, 'mounting_plane_id').then(
        () => {
          // damper plane change while NOT hood-bound also re-resolves the
          // fan_intake field set (§49.6:1685-1700) — handled by the
          // storage:accessory.position.model:change web in DamperForm.
          void refetchModel();
        }
      );
    },
    [createPosition, refetchModel, bump]
  );

  // ---- mounting popup state (§51.4) ----
  const [popupOpen, setPopupOpen] = useState(false);
  const openMountingPopup = useCallback(() => setPopupOpen(true), []);
  const closeMountingPopup = useCallback(() => setPopupOpen(false), []);

  // ---- shim (§50.2 / parity 342 — members #10-#13 are LIVE here) ----
  const fullyLoadingResolve = useRef<(() => void) | null>(null);
  const fullyLoading = useMemo(
    () =>
      new Promise<void>((res) => {
        fullyLoadingResolve.current = res;
      }),
    []
  );
  useEffect(() => {
    if (!loading) fullyLoadingResolve.current?.();
  }, [loading]);

  const shim = useMemo<ComponentFormShim | null>(() => {
    if (!model) return null;
    const view = viewRef.current;
    return {
      render: () => bump(),
      model,
      get rate() {
        return view.rate;
      },
      set rate(v: boolean) {
        view.rate = v;
      },
      catalogueFields: {},
      fan_rating_curves_model: null,
      createPosition: (type, planeId, mountingValue, changeTagName, extraData) =>
        createPosition(
          type,
          planeId,
          mountingValue,
          changeTagName ? 'mounting_plane_id' : undefined,
          extraData
        ),
      autoSize: (auto, m, data, faceVelocity) =>
        !!args.autoSize?.(
          !!auto,
          (m as FormModel) ?? model,
          (data as Record<string, unknown>) ?? {},
          faceVelocity,
          cmsAccessory
        ),
      dataGetterForRendering: (stagingPosition?: unknown, stagingModel?: unknown) =>
        args.dataGetterForRendering?.(stagingPosition, stagingModel) ?? {},
      defaultFaceVelocity: String(args.defaultFaceVelocity ?? DEFAULT_FACE_VELOCITY),
      catalogueFieldsInitializingPromise: fullyLoading,
      fullyLoading,
      // 6e2c-3 — surface the live position snapshot for the accessory selector's
      // staging_position_model clone (legacy current_model.position.toJSON()).
      getPosition: () => positionRef.current?.toJSON() ?? {},
      // 6e2c-3 fix — the readiness sibling of getPosition. The position boot
      // above runs a round trip AFTER this shim is registered, so a selector
      // that mounts with its form would otherwise clone an empty position.
      positionReady: () => positionSettled.current,
      // 6e2c-3 fix (Item 4) — the WRITE surface for the plane-UNCHANGED
      // position diff. Legacy selectAccessory:1177-1179 does
      // `current_model.position.set(positionChanges)`; the position model's
      // change pipeline auto-PATCHes ahu/positions[/<base>]/<id> with the diff.
      // We arm the live position model (a real user select armed the change)
      // then set the diff so the identical PATCH fires. no_fetch/echo flags and
      // the mounting_plane strip are handled inside AccessoryPositionModel.set.
      applyPositionDiff: (diff: Record<string, unknown>) => {
        const pos = positionRef.current;
        if (!pos || !diff || Object.keys(diff).length === 0) return;
        pos.markUserAction();
        pos.set(diff);
      },
      // 6e2c-3 fix — surface the section airway dims (legacy passes the full
      // section model on rating_mode:activate; the React opener only has {id}).
      // The selector needs these for the "Available Airway" readonly fields +
      // the §51.4 mounting math. awDimsRef holds the form's derived dims.
      getSectionDims: () => awDimsRef.current,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, fullyLoading, createPosition, cmsAccessory]);

  useEffect(() => {
    if (!shim) return undefined;
    Object.keys(catalogueFields).forEach((target) => {
      shim.catalogueFields[target] = catalogueFields[target].toShim();
    });
    return registerFormShim(row.id, shim);
  }, [shim, catalogueFields, row.id]);

  const position = positionRef.current;
  const currentPlane = useMemo(
    () =>
      position
        ? mountingPlanes.find((p) => p.id == Number(position.get('mounting_plane_id')))
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [position, mountingPlanes, positionVersion]
  );

  return {
    model,
    position,
    mounting: mountingRef.current,
    cmsAccessory,
    catalogueFields,
    subtabs,
    toggleSubtab,
    commitModelField,
    commitModelCheckbox,
    commitParamsField,
    commitPositionField,
    commitPositionCheckbox,
    commitMountingPlane,
    createPosition,
    popupOpen,
    openMountingPopup,
    closeMountingPopup,
    mountingPlanes,
    currentPlane,
    awDims,
    desiredFaceVelocity,
    loading,
    filterFieldsDisabled,
    shim,
    formRef,
    // ---- 6e2b-2 complex-type surface ----
    hoodIsMounting,
    markHoodIsMounting,
    forHoodMounting: !!cmsURLOverride && cmsFetching,
    intakeMode,
    setIntakeMode,
    setCmsURLOverride,
    sublistsGreyed,
    setInternalWalls,
    runAutoSize,
    autoSizeDisabled,
    setDesiredFaceVelocity,
  };
}
